import React, { useEffect, useRef, useState } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { TextPlugin } from 'gsap/TextPlugin';
import womanImage from '../assets/homepage/woman.png';
import bottomImage from '../assets/homepage/bottom.png';
import leftplant from '../assets/homepage/leftplant.png';
import rightplant from '../assets/homepage/rightplant.png';
import ball from '../assets/homepage/ball.png';
import lifebouy from '../assets/homepage/lifebouy.png';
import friendsMain from '../assets/homepage/friendsMain.png';
import homeMain from '../assets/homepage/homeMain.png';
import mapMain from '../assets/homepage/mapMain.png';

// Register GSAP plugins
gsap.registerPlugin(ScrollTrigger, TextPlugin);

// Note: SplitText is a premium GSAP plugin. For this implementation, 
// we'll create a simple text splitting function as an alternative
const splitTextIntoChars = (selector) => {
  const element = document.querySelector(selector);
  if (!element) return { chars: [] };
  
  const text = element.textContent;
  element.innerHTML = '';
  
  const chars = [];
  for (let i = 0; i < text.length; i++) {
    const char = text[i];
    const span = document.createElement('span');
    span.textContent = char === ' ' ? '\u00A0' : char; // Non-breaking space
    span.style.display = 'inline-block';
    element.appendChild(span);
    chars.push(span);
  }
  
  return { chars };
};

export function HomePageBody() {
  const [currentBanner, setCurrentBanner] = React.useState(1);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const cleanupFunctions = [];

    // Auto-switch banners every 5 seconds
    const bannerTimer = setInterval(() => {
      setCurrentBanner(prev => prev === 4 ? 1 : prev + 1);
    }, 5000);

    cleanupFunctions.push(() => clearInterval(bannerTimer));

    // Small delay to ensure DOM is ready
    const timer = setTimeout(() => {
      // Initialize animations
      const split = splitTextIntoChars("#title");

      // Parallax animations for images
      gsap.from("#woman", {
        scrollTrigger: {
          trigger: ".parallax",
          scrub: true,
        },
        y: 50,
      });

      gsap.from("#leftplant", {
        scrollTrigger: {
          trigger: ".parallax",
          scrub: true,
        },
        x: -150,
      });

      gsap.from("#rightplant", {
        scrollTrigger: {
          trigger: ".parallax",
          scrub: true,
        },
        x: 150,
      });

      gsap.from("#ball", {
        scrollTrigger: {
          trigger: ".parallax",
          scrub: true,
        },
        x: -200,
      });

      gsap.from("#lifebuoy", {
        scrollTrigger: {
          trigger: ".parallax",
          scrub: true,
        },
        x: 200,
      });

      // Title animation
      gsap
        .timeline({
          scrollTrigger: {
            trigger: ".parallax",
            start: "top 50%",
            end: "bottom top",
            toggleActions: "restart none none reset",
          },
        })
        .from(split.chars, {
          yPercent: -150,
          stagger: 0.05,
          duration: 0.7,
          ease: "back",
        })
        .from(split.chars, { opacity: 0, delay: 0.05, stagger: 0.05, duration: 0.2 }, 0);

    }, 500);

    return () => {
      clearTimeout(timer);
      // Run all cleanup functions
      cleanupFunctions.forEach(cleanup => cleanup());
      ScrollTrigger.getAll().forEach(st => st.kill());
    };
  }, []);

  return (
    <>
      <style>{`
        @import url("https://fonts.googleapis.com/css2?family=Comfortaa:wght@300;400;500;600;700&family=Mooli&display=swap");
        @import url('https://fonts.googleapis.com/css?family=Montserrat:400,700');
        @import url('https://fonts.googleapis.com/css?family=Roboto:400');
        @import url('https://fonts.googleapis.com/css?family=Kaushan+Script');

        @keyframes backgroundAnimation {
          0% {
            background-position: 0% 50%;
          }
          50% {
            background-position: 100% 50%;
          }
          100% {
            background-position: 0% 50%;
          }
        }

        @keyframes progressBarFill {
          from { margin-left: -100%; }
          to { margin-left: 0; }
        }

        * {
          box-sizing: border-box;
        }

        html, body {
          overflow-x: hidden;
          max-width: 100vw;
        }

        /* Main Header Styles */
        .main-header {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          z-index: 1000;
          font-family: Montserrat, sans-serif;
          color: #fff;
          background: transparent;
        }

        .header-wrapper {
          display: flex;
          align-items: center;
          justify-content: flex-start;
          flex-wrap: wrap;
          gap: 1.5em;
          width: 100%;
          margin: 0;
          padding: 1em 2em;
        }

        .main-logo {
          display: flex;
          align-items: center;
          gap: 0.75em;
          font-weight: 700;
          font-size: 1.875em;
        }

        .main-logo-image {
          width: 48px;
          height: 48px;
        }

        .main-logo-text {
          display: inline-block;
        }

        .main-nav {
          display: flex;
          align-items: center;
          flex: 0 0 auto;
        }

        .main-menu {
          display: flex;
          align-items: center;
          gap: 1.5em;
          list-style: none;
          margin: 0;
          padding: 0;
        }

        .main-menu li {
          padding: 0.75em 0;
        }

        .main-menu a {
          padding-bottom: 0.75em;
          text-transform: uppercase;
          color: #fff;
          font-size: 0.875em;
          text-decoration: none;
        }

        .main-menu a:hover {
          color: #fce38a;
          border-bottom: 0.1875em solid #fce38a;
          transition: .1s;
        }

        /* Mobile Menu & Header Styles */
        @media (max-width: 768px) {
          .header-wrapper {
            padding: 10px 15px;
            justify-content: space-between; /* Spreads items out */
            gap: 0; /* REMOVED GAP FOR MOBILE */
          }
          
          /* Move Hamburger to the Left */
          .hamburger-btn {
            display: block;
            color: white;
            font-size: 24px;
            background: none;
            border: none;
            cursor: pointer;
            padding: 5px;
            order: -1; /* MOVES BUTTON TO THE LEFT */
          }

          /* Shrink Logo Text */
          .main-logo {
            font-size: 1.2rem; /* Smaller font on mobile */
            margin-right: auto; /* Pushes logo slightly left-center */
            margin-left: 10px;
          }

          /* Fix Slider Overflow - Keep in one line */
          .controls {
            width: 100%;
            display: flex;
            justify-content: space-between;
            padding: 0 5px;
          }
          
          .controls label {
            width: auto;      /* Remove fixed 20% width */
            margin: 0 2px !important;    /* Force override of desktop margin */
            flex: 1;          /* Distribute space evenly */
            font-size: 0.7rem; /* Smaller font to fit text */
            padding-top: 1em;
          }
          
          .controls label span {
            font-size: 1.2em; /* Smaller numbers */
            display: block;   /* Stack number above text */
          }
          
          .main-nav {
            position: absolute;
            top: 100%;
            left: 0;
            width: 100%;
            background: rgba(0, 0, 0, 0.95);
            display: none;
            flex-direction: column;
            padding: 20px;
            z-index: 1001; /* Ensure it covers content */
          }
          
          .main-nav.open {
            display: flex;
          }
          
          .main-menu {
            flex-direction: column;
            gap: 1em;
          }
        }

        /* Hide hamburger on desktop */
        @media (min-width: 769px) {
          .hamburger-btn {
            display: none;
          }
        }

        /* Section-1 Styles */
        #section-1 {
          height: 38em;
          color: #fff;
          background-color: transparent;
          position: relative;
        }

        .content-slider {
          position: relative;
          width: 100%;
          height: 100%;
        }

        .slider {
          position: relative;
          width: inherit;
          height: inherit;
          overflow: hidden;
        }

        .banner {
          position: absolute;
          top: 0;
          left: 0;
          opacity: 0;
          z-index: 0;
          width: inherit;
          height: inherit;
          text-align: center;
          background-repeat: no-repeat;
          background-position: 50% 50%;
          background-size: cover;
          transition: opacity .5s ease;
        }

        .banner.active {
          opacity: 1;
          z-index: 1;
        }

        .banner-inner-wrapper {
          height: 100%;
          padding-top: 6em;
          background-image: linear-gradient(rgba(243,129,129,0), rgba(252,227,138,0));
          box-sizing: border-box;
        }

        .banner-inner-wrapper h2 {
          padding-bottom: 0.3em;
          font-family: 'Kaushan Script', cursive;
          font-weight: 400;
          font-size: 2.5em;
          text-transform: none;
        }

        .banner-inner-wrapper h1 {
          font-size: 6em;
          line-height: 95%;
          font-family: Montserrat, sans-serif;
          font-weight: 700;
          text-transform: uppercase;
          margin: 0;
        }

        .banner-inner-wrapper .line {
          display: block;
          width: 4em;
          height: 0.1875em;
          margin: 2.5em auto;
          background: #fff;
        }

        /* Mobile Typography */
        @media (max-width: 768px) {
          .banner-inner-wrapper h1 {
            font-size: 2.5em;
            line-height: 1.1;
          }
          .banner-inner-wrapper h2 {
            font-size: 1.5em;
          }
          .banner-inner-wrapper .line {
            margin: 1.5em auto;
          }
        }

        .learn-more-button {
          padding-bottom: 5em;
          z-index: 15;
        }

        .learn-more-button a {
          padding: 0.5em 2em;
          text-align: center;
          font-family: Montserrat, sans-serif;
          font-size: 0.875em;
          color: #fff;
          text-transform: uppercase;
          border: 0.1875em solid #fff;
          text-decoration: none;
        }

        .learn-more-button a:hover {
          color: #e88382;
          border-color: #e88382;
          transition: .3s;
        }

        #top-banner-1 {
          background: url('https://checkboxes-demo.webdevs.co.ua/images/mogo/banner-1.png') no-repeat center center;
          background-size: cover;
        }

        #top-banner-2 {
          background: url('https://checkboxes-demo.webdevs.co.ua/images/mogo/banner-2.png') no-repeat center center;
          background-size: cover;
        }

        #top-banner-3 {
          background: url('https://checkboxes-demo.webdevs.co.ua/images/mogo/banner-3.png') no-repeat center center;
          background-size: cover;
        }

        #top-banner-4 {
          background: url('https://checkboxes-demo.webdevs.co.ua/images/mogo/banner-4.png') no-repeat center center;
          background-size: cover;
        }

        .content-slider nav {
          position: absolute;
          bottom: 0.5em;
          width: 100%;
          z-index: 10;
          text-align: center;
        }

        .controls {
          display: block;
          width: 70%;
          margin: 0 auto;
          font-family: Montserrat, sans-serif;
          color: #fff;
        }

        .controls label {
          position: relative;
          display: inline-block;
          width: 20%;
          height: 3.1em;
          overflow: hidden;
          margin: 0 1em;
          padding-top: 1em;
          text-align: left;
          text-transform: uppercase;
          font-family: Montserrat, sans-serif;
          font-size: 1em;
          color: #f6eac5;
          font-weight: 400;
          cursor: pointer;
          transition: all .3s;
        }

        .controls label:hover {
          color: #e88382;
        }

        .controls label.active {
          cursor: default;
          color: #fff;
        }

        .progressbar {
          position: absolute;
          top: 0;
          left: 0;
          height: 3px;
          width: 100%;
          background: #f6eac5;
          z-index: 100;
        }

        .controls label.active .progressbar {
          background: #fff;
        }

        .progressbar-fill {
          position: absolute;
          width: 100%;
          height: inherit;
          margin-left: -100%;
          background: #e88382;
        }

        .controls label.active .progressbar-fill {
          animation: progressBarFill 5s linear;
        }

        .controls label span {
          font-size: 1.4em;
          font-weight: 700;
        }

        .parallax {
          position: relative;
          display: flex;
          justify-content: center;
          align-items: center;
          height: 150vh;
          width: 100vw;
          max-width: 100%;
          overflow: hidden;
          background: rgb(206, 245, 223);
          background: radial-gradient(
            circle,
            rgba(206, 245, 223, 0.4) 34%,
            rgba(206, 245, 223, 1) 68%
          );
        }

        .parallax img {
          position: absolute;
          bottom: 0;
          left: 0;
          width: 100%;
          pointer-events: none;
        }

        #title {
          position: absolute;
          top: 13%;
          font-size: clamp(2rem, 6vw, 4rem);
          color: #000;
          z-index: 50;
          font-family: "Comfortaa", cursive;
        }

        .blog h2 {
          font-size: clamp(2rem, 6vw, 3rem);
          color: #000;
          margin: 60px 0 40px;
          text-align: center;
          font-family: "Comfortaa", cursive;
        }

        .blog p {
          font-size: clamp(1rem, 6vw, 1.3rem);
          color: #000;
          font-weight: 400;
          margin-bottom: 30px;
          text-align: center;
          line-height: 1.5;
          font-family: "Mooli", sans-serif;
        }

        .feature-section {
          width: 100%;
          max-width: 1200px;
          margin: 0 auto;
          padding: 80px 20px;
        }

        .feature-row {
          display: flex;
          align-items: center;
          margin-bottom: 80px;
          gap: 40px;
        }

        .feature-row:nth-child(even) {
          flex-direction: row-reverse;
        }

        .feature-image {
          flex: 1;
        }

        .feature-image img {
          width: 100%;
          border-radius: 16px;
          box-shadow: 0 10px 30px rgba(0,0,0,0.1);
        }

        .feature-text {
          flex: 1;
          padding: 0 20px;
        }

        .feature-text h3 {
          font-family: "Comfortaa", cursive;
          font-size: 2.5rem;
          margin-bottom: 20px;
        }

        .feature-text p {
          font-family: "Mooli", sans-serif;
          font-size: 1.1rem;
          line-height: 1.6;
          color: #333;
        }

        @media (max-width: 768px) {
          .feature-row {
            flex-direction: column !important; /* Stack on mobile */
          }
          .feature-text h3 {
            font-size: 2rem;
          }
        }
        
        .header-logo {
          position: fixed;
          top: 20px;
          left: 20px;
          z-index: 1000;
          display: flex;
          align-items: center;
          gap: 12px;
          background: rgba(255, 255, 255, 0.95);
          padding: 8px 16px;
          border-radius: 25px;
          backdrop-filter: blur(10px);
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
          transition: all 0.3s ease;
        }
        
        .header-logo:hover {
          background: rgba(255, 255, 255, 1);
          transform: translateY(-2px);
          box-shadow: 0 6px 25px rgba(0, 0, 0, 0.15);
        }
        
        .header-logo img {
          width: 32px;
          height: 32px;
        }
        
        .header-logo .brand-text {
          font-family: 'Arial', sans-serif;
          font-weight: 700;
          font-size: 16px;
          color: #1976d2;
          text-decoration: none;
        }
        
        @media (max-width: 768px) {
          .header-logo {
            top: 15px;
            left: 15px;
            padding: 6px 12px;
            gap: 8px;
          }
          
          .header-logo img {
            width: 28px;
            height: 28px;
          }
          
          .header-logo .brand-text {
            font-size: 14px;
          }
        }
      `}</style>

      <div style={{ width: '100vw', maxWidth: '100%', overflowX: 'hidden' }}>
        {/* Main Header */}
        <header className="main-header">
          <div className="header-wrapper">
            <div className="main-logo">
              <img src="/PelicanLogo.png" alt="Pelican Beaks logo" className="main-logo-image" />
              <span className="main-logo-text">Pelican Beaks</span>
            </div>
            <button 
              className="hamburger-btn"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Toggle menu"
            >
              ☰
            </button>
            <nav className={`main-nav ${mobileMenuOpen ? 'open' : ''}`}>
              <ul className="main-menu">
                <li><a href="about" onClick={() => setMobileMenuOpen(false)}>About</a></li>
                <li><a href="pricing" onClick={() => setMobileMenuOpen(false)}>Pricing</a></li>
                {/* <li><a href="#blog-section">Blog</a></li> */}
                <li><a href="contact" onClick={() => setMobileMenuOpen(false)}>Contact</a></li>
              </ul>
            </nav>
          </div>
        </header>

        {/* Section-1 Slider */}
        <section id="section-1">
          <div className="content-slider">
            <div className="slider">
              {/* Banner 1 */}
              <div className={`banner ${currentBanner === 1 ? 'active' : ''}`} id="top-banner-1">
                <div className="banner-inner-wrapper">
                  <h2>Dreaming of a Trip?</h2>
                  <h1>Your Personal<br/>AI Travel Planner</h1>
                  <div className="line"></div>
                  <div className="learn-more-button">
                    <a href="#features-section">Learn More</a>
                  </div>
                </div>
              </div>
              {/* Banner 2 */}
              <div className={`banner ${currentBanner === 2 ? 'active' : ''}`} id="top-banner-2">
                <div className="banner-inner-wrapper">
                  <h2>Smart & Fast</h2>
                  <h1>Instant<br/>Itinerary Generator</h1>
                  <div className="line"></div>
                  <div className="learn-more-button">
                    <a href="#features-section">Learn More</a>
                  </div>
                </div>
              </div>
              {/* Banner 3 */}
              <div className={`banner ${currentBanner === 3 ? 'active' : ''}`} id="top-banner-3">
                <div className="banner-inner-wrapper">
                  <h2>Budget Friendly</h2>
                  <h1>Optimize Your<br/>Travel Costs</h1>
                  <div className="line"></div>
                  <div className="learn-more-button">
                    <a href="#features-section">Learn More</a>
                  </div>
                </div>
              </div>
              {/* Banner 4 */}
              <div className={`banner ${currentBanner === 4 ? 'active' : ''}`} id="top-banner-4">
                <div className="banner-inner-wrapper">
                  <h2>Ready to go?</h2>
                  <h1>Start Your<br/>Adventure Now</h1>
                  <div className="line"></div>
                  <div className="learn-more-button">
                    <a href="#features-section">Learn More</a>
                  </div>
                </div>
              </div>
            </div>
            {/* Add spacing between button and controls (Desktop only) */}
            <style>
              {`
                .controls {
                  margin-top: 40px; /* Moves nav down from the button */
                }
                @media (max-width: 768px) {
                  .controls {
                    margin-top: 0; /* Keeps mobile layout compact/unchanged */
                  }
                }
              `}
            </style>
            <nav>
              <div className="controls">
                <label onClick={() => setCurrentBanner(1)} className={currentBanner === 1 ? 'active' : ''}>
                  <span className="progressbar"><span className="progressbar-fill"></span></span>
                  <span>01</span> Intro
                </label>
                <label onClick={() => setCurrentBanner(2)} className={currentBanner === 2 ? 'active' : ''}>
                  <span className="progressbar"><span className="progressbar-fill"></span></span>
                  <span>02</span> Work
                </label>
                <label onClick={() => setCurrentBanner(3)} className={currentBanner === 3 ? 'active' : ''}>
                  <span className="progressbar"><span className="progressbar-fill"></span></span>
                  <span>03</span> About
                </label>
                <label onClick={() => setCurrentBanner(4)} className={currentBanner === 4 ? 'active' : ''}>
                  <span className="progressbar"><span className="progressbar-fill"></span></span>
                  <span>04</span> Contacts
                </label>
              </div>
            </nav>
          </div>
        </section>

        <div id="features-section" className="feature-section">
          <div style={{textAlign: 'center', marginBottom: '60px'}}>
            <h2 style={{fontFamily: '"Comfortaa", cursive', fontSize: 'clamp(2rem, 6vw, 3rem)', color: '#000'}}>Your Pocket Travel Companion</h2>
            <p style={{fontFamily: '"Mooli", sans-serif', fontSize: 'clamp(1rem, 6vw, 1.3rem)', color: '#000'}}>Are you ready to embark on an adventure like no other?</p>
          </div>
          <div className="feature-row">
              <div className="feature-image">
                  <img src={homeMain} alt="Smart Itineraries"/>
              </div>
              <div className="feature-text">
                  <h3>Smart Itineraries</h3>
                  <p>Let AI craft your perfect adventure with personalized recommendations and detailed itineraries. Organize every detail from flights to dining with intelligent scheduling and local insights.</p>
              </div>
          </div>
          <div className="feature-row">
              <div className="feature-image">
                  <img src={mapMain} alt="Discover and Explore"/>
              </div>
              <div className="feature-text">
                  <h3>Discover & Explore</h3>
                  <p>Find inspiration from fellow travelers and customize their adventures for your own journey. Explore new destinations with our interactive map and discover hidden gems.</p>
              </div>
          </div>
          <div className="feature-row">
              <div className="feature-image">
                  <img src={friendsMain} alt="Social Sharing"/>
              </div>
              <div className="feature-text">
                  <h3>Social Sharing</h3>
                  <p>Connect with friends, share your travel plans, and collaborate on unforgettable group adventures. See where your friends are headed and get inspired for your next trip.</p>
              </div>
          </div>
      </div>
      </div>
    </>
  );
}

export default HomePageBody;
